import { useState } from "react";
import { AppRoutes } from "@/infra/router";
import type { TermsAndConditionsResponse } from "types/schemas";
import { mapLocaleToLanguage } from "@akina/domain";
import { useFetchLegalPage, useUpdateTermsAndConditionsStatus } from "@akina/infra";
import { useLocaleStore } from "@akina/presentation";
import { LegalAcceptanceCheckboxRow } from "./LegalAcceptanceCheckboxRow";
import { LegalAcceptanceModalLayout } from "./LegalAcceptanceModalLayout";
import { LegalExternalLink } from "./LegalExternalLink";

type UpdatedPrivacyPolicyAcceptanceModalProps = {
  open: boolean;
  onOpenChange: (prev: boolean) => void;
  patientTerms: TermsAndConditionsResponse;
};

export function UpdatedPrivacyPolicyAcceptanceModal({
  open,
  onOpenChange,
  patientTerms,
}: Readonly<UpdatedPrivacyPolicyAcceptanceModalProps>) {
  const { locale } = useLocaleStore();
  const language = mapLocaleToLanguage(locale);
  const { data: privacyPolicyLegalPage } = useFetchLegalPage(
    "privacy-policy-and-data-transparency",
    "care",
    language,
  );
  const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] = useState(false);
  const [errorPrivacyPolicy, setErrorPrivacyPolicy] = useState(false);

  const updateStatus = useUpdateTermsAndConditionsStatus({
    onSuccess: () => onOpenChange(false),
  });

  const handleAccept = () => {
    if (acceptPrivacyPolicy && privacyPolicyLegalPage) {
      updateStatus.mutate({
        dataPrivacyPolicyAccepted: true,
        dataPrivacyPolicyAcceptedTimestamp: new Date().toISOString(),
        termsAndConditionsAccepted: patientTerms.termsAndConditionsAccepted,
        termsAndConditionsAcceptedTimestamp: patientTerms.termsAndConditionsAcceptedTimestamp,
        privacyPolicyVersion: privacyPolicyLegalPage.version,
        termsVersion: patientTerms.termsVersion,
      });
    } else {
      setErrorPrivacyPolicy(true);
    }
  };

  return (
    <LegalAcceptanceModalLayout
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen && !acceptPrivacyPolicy) setErrorPrivacyPolicy(true);
        else onOpenChange(isOpen);
      }}
      titleMessageId="legal.updatedPrivacyPolicyAcceptance.title"
      descriptionMessageId="legal.updatedPrivacyPolicyAcceptance.description"
      errorMessageId="legal.updatedPrivacyPolicyAcceptance.error"
      buttonMessageId="legal.button"
      hasError={errorPrivacyPolicy}
      isLoading={updateStatus.isPending}
      onAccept={handleAccept}>
      <LegalAcceptanceCheckboxRow
        checked={acceptPrivacyPolicy}
        onCheckedChange={setAcceptPrivacyPolicy}
        onErrorClear={() => setErrorPrivacyPolicy(false)}
        labelMessageId="legal.label">
        <LegalExternalLink
          route={AppRoutes.PrivacyPolicyAndDataTransparency}
          messageId="legal.privacyPolicy"
        />
      </LegalAcceptanceCheckboxRow>
    </LegalAcceptanceModalLayout>
  );
}
